import { MenuItem, Select, SelectProps } from '@mui/material';
import { Controller } from 'react-hook-form';

import { cn } from '../../utils/cn';
import LightTooltip from './LightTooltip';

export interface Values {
	value: string | number;
	label: string | number;
	disabled?: boolean;
}

function booleanFormValueToSelectString(value: unknown): '' | 'true' | 'false' {
	if (value === true || value === 'true') return 'true';
	if (value === false || value === 'false') return 'false';
	return '';
}

type Props = {
	title: string;
	values?: Values[];
	width?: string;
	height?: string;
	control: any;
	name: string;
	/** Useful when it is a boolean selection. For this to work, the values must be 'true' or 'false' only. */
	valueAsBoolean?: boolean;
	changeItemColor?: boolean;
	changeItems?: string[];
	LabelClassName?: string;
	containerClassName?: string;
	fieldError?: any;
	errorMessage?: string;
	tooltipMessage?: string;
	disabledStyle?: 'default' | 'gray-filled';
} & SelectProps;

const ControlledSimpleSelect = ({
	title,
	values,
	width,
	height,
	name,
	valueAsBoolean,
	changeItemColor,
	changeItems,
	LabelClassName,
	containerClassName,
	fieldError,
	errorMessage,
	tooltipMessage,
	disabledStyle = 'default',
	...rest
}: Props) => {
	return (
		<div className={containerClassName}>
			<LightTooltip title={tooltipMessage}>
				<div className={cn('text-[12px] text-[#4A4A4A]', LabelClassName)}>
					{title}
					{rest.required && <span className="text-red">*</span>}
				</div>
			</LightTooltip>
			<Controller
				render={({ field: { onChange, onBlur, value, ref } }) => (
					<Select
						{...rest}
						sx={{
							width: `${width || '27rem'}`,
							height: `${height || '2rem'}`,
							fontSize: '12px',
							fontFamily: 'Graphie, sans-serif',
							cursor: rest.readOnly ? 'not-allowed' : 'pointer',
							pointerEvents: rest.readOnly ? 'none' : 'auto',
							...(disabledStyle === 'gray-filled' && {
								'& .Mui-disabled': {
									'-webkit-text-fill-color': 'var(--color-gray-600)',
								},
							}),
						}}
						displayEmpty
						multiple={rest.multiple}
						inputRef={ref}
						disabled={rest.disabled || !values}
						defaultValue={rest.defaultValue}
						onChange={(e, a) => {
							if (valueAsBoolean) {
								onChange(String(e.target.value) === 'true');
							} else {
								onChange(e);
							}
							rest.onChange?.(e, a);
						}}
						variant={rest?.variant || 'outlined'}
						onBlur={onBlur}
						value={
							valueAsBoolean
								? booleanFormValueToSelectString(value)
								: (value ?? (rest.multiple ? [] : ''))
						}
						inputProps={{ 'aria-label': 'Without label' }}
						className={cn(
							'read-only:cursor-not-allowed',
							rest.disabled &&
								disabledStyle === 'gray-filled' &&
								'text-gray-600 bg-[#F5F5F5]',
						)}
					>
						<MenuItem
							sx={{ fontSize: '12px', display: 'none' }}
							value=""
							disabled
						>
							{' '}
						</MenuItem>
						<MenuItem
							sx={{ fontSize: '12px', display: 'none' }}
							value={0}
							disabled
						>
							{' '}
						</MenuItem>
						<MenuItem
							sx={{ fontSize: '12px', display: 'none' }}
							value={-2}
							disabled
						>
							{' '}
						</MenuItem>
						{values?.map((el) => {
							if (changeItemColor) {
								return (
									<MenuItem
										sx={{
											fontSize: '12px',
											fontWeight: changeItems?.includes(el.value.toString())
												? 'bold'
												: '',
										}}
										key={el.value}
										value={el.value}
										disabled={el.disabled}
									>
										{el.label}
									</MenuItem>
								);
							} else {
								return (
									<MenuItem
										sx={{
											fontSize: '12px',
										}}
										key={el.value}
										value={el.value}
										disabled={el.disabled}
									>
										{el.label}
									</MenuItem>
								);
							}
						})}
					</Select>
				)}
				name={name}
				disabled={rest.disabled}
				control={rest.control}
			/>
			{fieldError && (
				<div className="text-[10px] text-[#dd0000]">{errorMessage}</div>
			)}
		</div>
	);
};

export default ControlledSimpleSelect;
